import { useEffect, useState, useRef } from "react";
import categorySoundCombos from "../Sound_Combinations/categorySoundCombos";
import useSoundStore from "../store/useSoundStore";
import { SoundsInfo } from "../Lists/SoundInfo";
import {
  ProductivityIcon, RandomIcon, RelaxIcon, MotivationIcon, FocusIcon,
  CodeIcon, SleepIcon, MeditationIcon, StudyIcon
} from "../Icons";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";


const categories = [
  { title: "Productivity", name: "productivity", icon: <ProductivityIcon /> },
  { title: "Random", name: "random", icon: <RandomIcon /> },
  { title: "Relax", name: "relax", icon: <RelaxIcon /> },
  { title: "Coding", name: "coding", icon: <CodeIcon /> },
  { title: "Sleep", name: "sleep", icon: <SleepIcon /> },
  { title: "Meditation", name: "meditation", icon: <MeditationIcon /> },
  { title: "Focus", name: "focus", icon: <FocusIcon /> },
  { title: "Motivation", name: "motivation", icon: <MotivationIcon /> },
  { title: "Study", name: "study", icon: <StudyIcon /> },
];

const wavePatterns = [
  ["M0 24C35 94 85 83 170 40V110H0Z", "M0 67C45 56 36 123 100 97C126 89 151 92 170 78V110H0Z"],
  ["M0 56C45 14 100 22 170 72V110H0Z", "M0 89C48 110 88 50 170 94V110H0Z"],
  ["M0 42C40 20 60 103 110 65C138 44 156 44 170 53V110H0Z", "M0 82C48 113 106 67 170 88V110H0Z"],
  ["M0 70C50 100 107 12 170 31V110H0Z", "M0 97C60 55 116 115 170 73V110H0Z"],
];

const CategorySlider = ({ playMix }) => {
  const stripRef = useRef(null);
  const resetSignal = useSoundStore((state) => state.resetSignal);
  const [activeCategory, setActiveCategory] = useState(null);
  const [comboIndex, setComboIndex] = useState(0);
  const [waveVariation, setWaveVariation] = useState(0);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  useEffect(() => {
    const strip = stripRef.current;
    const updateEdges = () => {
      setCanScrollBack(strip.scrollLeft > 1);
      setCanScrollForward(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1);
    };
    updateEdges();
    strip.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateEdges) : null;
    observer?.observe(strip);
    return () => {
      strip.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
      observer?.disconnect();
    };
  }, []);

  const scrollPlaylists = (direction) => {
    const strip = stripRef.current;
    const cardWidth = strip.firstElementChild.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 8;
    const count = Math.max(1, Math.min(3, Math.floor(strip.clientWidth / (cardWidth + gap))));
    strip.scrollBy({ left: direction * count * (cardWidth + gap), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  useEffect(() => {
    setActiveCategory(null);
    setComboIndex(0);
    setWaveVariation(0);
  }, [resetSignal]);

  const playCombo = (combo) => {
    playMix(combo.map(({ sound, volume }) => ({
      index: SoundsInfo.findIndex((item) => item.Sound === sound),
      volume,
    })));
  };

  const handleCategoryClick = (name) => {
    const combos = categorySoundCombos[name];
    if (!combos?.length) return;

    const isSame = activeCategory === name;

    if (isSame) {
      setActiveCategory(null);
      setComboIndex(0);
      setWaveVariation(0);
      playMix([]);
    } else {
      playCombo(combos[0]);
      setActiveCategory(name);
      setComboIndex(0);
      setWaveVariation(0);
    }
  };

  const handleNextComboClick = (name) => {
    if (!activeCategory || activeCategory !== name) return;

    const combos = categorySoundCombos[name];
    const next = (comboIndex + 1) % combos.length;
    playCombo(combos[next]);
    setComboIndex(next);
    setWaveVariation((previous) => (previous + 1) % wavePatterns.length);
  };

  return (
    <div className={`playlist-carousel ${canScrollBack ? "can-scroll-back" : ""} ${canScrollForward ? "can-scroll-forward" : ""}`}>
      <div ref={stripRef} className="playlist-strip">
        {categories.map(({ name, title, icon }) => {
          const isActive = activeCategory === name;
          return (
            <div key={name} className="playlist-item">
              <button type="button" aria-pressed={isActive} onClick={() => handleCategoryClick(name)} className={`playlist-card ${isActive ? "is-active" : ""}`}>
                <svg key={isActive ? waveVariation : "idle"} className="playlist-waves" viewBox="0 0 170 110" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                  {wavePatterns[isActive ? waveVariation : 0].map((path, index) => <path key={index} d={path} />)}
                </svg>
                <span className="playlist-icon" aria-hidden="true">{icon}</span>
                <span className="playlist-title">{title}</span>
              </button>
              {isActive && <button type="button" onClick={() => handleNextComboClick(name)} className="next-mix-button" aria-label={`Next ${title} mix`}>Next</button>}
            </div>
          );
        })}
      </div>
      {canScrollBack && <button type="button" className="carousel-arrow carousel-prev" aria-label="Previous playlists" onClick={() => scrollPlaylists(-1)}><FaChevronLeft aria-hidden="true" /></button>}
      {canScrollForward && <button type="button" className="carousel-arrow carousel-next" aria-label="More playlists" onClick={() => scrollPlaylists(1)}><FaChevronRight aria-hidden="true" /></button>}
    </div>
  );
};

export default CategorySlider;
